import { useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { addDays, newId } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useAssets } from '../data/fleet.ts'
import { usePersons } from '../data/people.ts'
import { usePlan } from '../data/plan.ts'
import { longDay, machineLabel } from '../data/planUi.ts'
import { useWorkOrders } from '../data/safety.ts'
import { formatDuration, useWorkIntervals, useWorkSessions } from '../data/work.ts'
import { buildDays, clockOf, pendingEntries, spanOf, spanProblem, type TimeEntry } from '../data/workTime.ts'
import { formatLocal, localDate } from '../lib/time.ts'
import { BackLink, Banner, errorText, Field, PendingBanner, Pill } from '../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { useNow } from '../ui/useNow.ts'

const MY_DAYS = 14
const TEAM_DAYS = 7
const REVIEW_TONE = { pending: 'amber', accepted: 'green', rejected: 'red' } as const

/** My time (GK-016, GK-018): what the server holds for me, as the device has it, by local day. The head greenkeeper can switch to the team's time and accept or reject what
 *  waits for review. A correction replaces a period (never edits it) and is an intent: the server answers, the new rows arrive with the next sync. */
export function MyTimePage() {
  const { t, i18n } = useTranslation()
  const { site, personId, kiosk, can } = useClub()
  const lang = i18n.language; const tz = site.timezone
  const me = kiosk?.session?.personId ?? personId
  const head = can('worklog.review')
  const record = useWriteAccess('worklog.record')
  const intervals = useWorkIntervals(); const sessions = useWorkSessions(); const persons = usePersons(); const orders = useWorkOrders(); const assets = useAssets()
  const { ready, plan } = usePlan(lang)
  const now = useNow(15_000)
  const [view, setView] = useState<'me' | 'team'>('me')
  const [replaced, setReplaced] = useState(false)
  /** periods the person has just corrected: they stay in place, with their message, until the list is left (the next sync marks them replaced) */
  const [kept, setKept] = useState<ReadonlySet<string>>(new Set())
  if (!intervals || !sessions || !persons || !orders || !assets || !ready) return <p>{t('common.loading')}</p>
  const team = head && view === 'team'
  const days = buildDays(intervals, { tz, now, today: localDate(now, tz), days: team ? TEAM_DAYS : MY_DAYS, personId: me, everybody: team, showReplaced: replaced, keep: kept, skipPending: team })
  const waiting = team ? pendingEntries(intervals, tz, now, kept) : []
  const sessionById = new Map(sessions.map((s) => [s.id, s]))
  const nameOf = new Map(persons.map((p) => [p.id, p.display_name]))
  const titleOf = (e: TimeEntry): string => {
    const s = sessionById.get(e.row.work_session_id)
    if (s?.job_id) return plan.byId.get(s.job_id)?.title_text ?? t('myTime.job')
    if (!s?.work_order_id) return t('myTime.job')
    const wo = orders.find((o) => o.id === s.work_order_id); const asset = wo ? assets.find((a) => a.id === wo.asset_id) : undefined
    return asset ? `${t('myTime.workshop')} · ${machineLabel(asset)}` : t('myTime.workshop')
  }
  const row = (e: TimeEntry, showDay: boolean) => (
    <TimeRow key={e.row.id} e={e} tz={tz} now={now} title={titleOf(e)} who={team ? nameOf.get(e.row.person_id) ?? '?' : null} showDay={showDay}
      canCorrect={record.allowed && !kiosk && !e.replaced && !!e.to && !e.row._pending} canDecide={team && e.row.review_state === 'pending' && !e.replaced && !e.row._pending}
      onSend={(id) => setKept((k) => new Set(k).add(id))} />
  )
  return (
    <section className="flex flex-col gap-4" data-testid="mytime-page">
      <BackLink to="/">← {t('common.today')}</BackLink>
      <div>
        <h1 className="text-2xl font-bold">{t('myTime.title')}</h1>
        <p className="meta">{t('myTime.window', { count: team ? TEAM_DAYS : MY_DAYS })}</p>
      </div>
      {head && (
        <div role="group" aria-label={t('myTime.viewLabel')} className="grid grid-cols-2 gap-2">
          <button type="button" aria-pressed={!team} className={`btn !min-h-11 ${team ? '' : 'btn-primary'}`} onClick={() => setView('me')} data-testid="mytime-me-toggle">{t('myTime.view.me')}</button>
          <button type="button" aria-pressed={team} className={`btn !min-h-11 ${team ? 'btn-primary' : ''}`} onClick={() => setView('team')} data-testid="mytime-team-toggle">{t('myTime.view.team')}</button>
        </div>
      )}
      <label className="flex min-h-14 items-center gap-3 text-sm font-medium">
        <input type="checkbox" className="size-5" checked={replaced} onChange={(e) => setReplaced(e.target.checked)} data-testid="mytime-replaced-toggle" />
        {t('myTime.showReplaced')}
      </label>
      {waiting.length > 0 && (
        <section className="flex flex-col gap-2" data-testid="mytime-pending">
          <h2 className="text-lg font-bold">{t('myTime.pendingTitle')}</h2>
          <ul className="flex flex-col gap-2">{waiting.map((e) => row(e, true))}</ul>
        </section>
      )}
      {days.length === 0 && waiting.length === 0 && <p className="opacity-70" data-testid="mytime-empty">{t('myTime.empty')}</p>}
      {days.map((d) => (
        <section key={d.day} className="flex flex-col gap-2" data-testid="mytime-day" data-day={d.day}>
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <h2 className="text-lg font-bold first-letter:uppercase">{longDay(d.day, lang)}</h2>
            {!team && <span className="font-semibold tabular-nums" data-testid="mytime-total">{t('myTime.total', { time: formatDuration(d.totalMs) })}</span>}
          </div>
          {!team && d.pendingMs > 0 && <p className="text-sm opacity-80" data-testid="mytime-pending-part">{t('myTime.pendingPart', { time: formatDuration(d.pendingMs) })}</p>}
          <ul className="flex flex-col gap-2">{d.entries.map((e) => row(e, false))}</ul>
        </section>
      ))}
      {/* M23 item 55: the way to add forgotten hours comes after the hours, not before them */}
      {record.permitted && !team && <Link to="/work/manual" className="btn" data-testid="mytime-add">{t('myTime.add')}</Link>}
    </section>
  )
}

function TimeRow({ e, tz, now, title, who, showDay, canCorrect, canDecide, onSend }: {
  e: TimeEntry; tz: string; now: number; title: string; who: string | null; showDay: boolean; canCorrect: boolean; canDecide: boolean; onSend: (id: string) => void
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const r = e.row
  const end = e.to ? (localDate(e.to, tz) === e.day ? clockOf(e.to, tz) : formatLocal(e.to, tz, 'dd/MM HH:mm')) : ''
  const strike = e.replaced ? 'line-through opacity-60' : ''
  return (
    <li className="card flex flex-col gap-2" data-testid="mytime-row" data-interval={r.id} data-state={e.replaced ? 'replaced' : e.running ? 'running' : r.review_state}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className={`min-w-0 break-words font-semibold ${strike}`}>{title}</span>
        <span className={`font-mono tabular-nums ${strike}`} data-testid="mytime-times">{showDay ? formatLocal(e.from, tz, 'dd/MM HH:mm') : clockOf(e.from, tz)}–{end}</span>
      </div>
      {who && <p className="text-sm font-semibold" data-testid="mytime-who">{who}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`font-semibold tabular-nums ${strike}`} data-testid="mytime-duration">{formatDuration(e.ms)}</span>
        {e.running && <Pill tone="green">{t('myTime.running')}</Pill>}
        {e.replaced && <Pill tone="gray">{t('myTime.replaced')}</Pill>}
        {r.source !== 'timer' && <Pill tone="gray">{t(`myTime.source.${r.source}`)}</Pill>}
        {r.review_state !== 'none' && <Pill tone={REVIEW_TONE[r.review_state]}>{t(`myTime.review.${r.review_state}`)}</Pill>}
        {r.exception_code && <Pill tone="amber">{r.exception_code === 'WORK_INTERVAL_OVERLAP' ? t('myTime.overlap') : errorText(t, r.exception_code)}</Pill>}
        {r._pending && <Pill tone="amber">{t('common.pendingSend')}</Pill>}
      </div>
      {r.note && <p className="meta">{r.note}</p>}
      {canDecide && <Decide id={r.id} />}
      {canCorrect && !editing && <button type="button" className="btn self-start !px-4" onClick={() => setEditing(true)} data-testid="mytime-correct">{t('myTime.correct')}</button>}
      {editing && <CorrectForm e={e} tz={tz} now={now} onSend={onSend} onClose={() => setEditing(false)} />}
    </li>
  )
}

/** The head greenkeeper's decision on a period that waits for review. Once decided, the period leaves "waiting" with the next sync, which is the confirmation. */
function Decide({ id }: { id: string }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const decide = (decision: 'accepted' | 'rejected') => void w.run('work.review@1', { interval_id: id, decision }, decision === 'accepted' ? 'myTime.accepted' : 'myTime.rejected')
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className="btn btn-primary !min-h-11" disabled={w.busy} onClick={() => decide('accepted')} data-testid="time-accept">{t('myTime.accept')}</button>
        <button type="button" className="btn !min-h-11" disabled={w.busy} onClick={() => decide('rejected')} data-testid="time-reject">{t('myTime.reject')}</button>
      </div>
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error">{w.error}</Banner>}
    </div>
  )
}

function CorrectForm({ e, tz, now, onSend, onClose }: { e: TimeEntry; tz: string; now: number; onSend: (id: string) => void; onClose: () => void }) {
  const { t, i18n } = useTranslation()
  const w = useWorkshopAction()
  const newIntervalId = useRef(newId())
  const [from, setFrom] = useState(clockOf(e.from, tz))
  const [to, setTo] = useState(e.to ? clockOf(e.to, tz) : '')
  const [note, setNote] = useState('')
  const id = e.row.id
  const span = spanOf(e.day, from, to, tz)
  const problem = spanProblem(span, now)
  const send = () => {
    if (!span || problem) return
    onSend(id)
    void w.run('work.correct@1', { interval_id: id, new_interval_id: newIntervalId.current, started_at: span.startedAt, ended_at: span.endedAt, note: note.trim() || null }, 'myTime.corrected')
  }
  if (w.ok) return <Banner tone="ok"><span data-testid="mytime-corrected">{w.ok}</span></Banner>
  return (
    <form className="flex flex-col gap-3 rounded-xl border-2 border-slate-300 p-3 dark:border-slate-600" data-testid="mytime-correct-form" onSubmit={(ev) => { ev.preventDefault(); send() }}>
      <p className="text-sm font-medium">{t('myTime.correctHelp', { day: longDay(e.day, i18n.language) })}</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('myTime.correctFrom')} htmlFor={`cf-${id}`}><input id={`cf-${id}`} type="time" className="input" value={from} onChange={(ev) => setFrom(ev.target.value)} data-testid="mytime-correct-from" /></Field>
        <Field label={t('myTime.correctTo')} htmlFor={`ct-${id}`}><input id={`ct-${id}`} type="time" className="input" value={to} onChange={(ev) => setTo(ev.target.value)} data-testid="mytime-correct-to" /></Field>
      </div>
      {span?.nextDay && !problem && <p className="text-sm font-medium">{t('myTime.nextDay', { day: longDay(addDays(e.day, 1), i18n.language) })}</p>}
      {problem && problem !== 'missing' && <p role="alert" className="text-sm font-medium text-red-700">{t(`workManual.problem.${problem}`)}</p>}
      <Field label={t('myTime.correctNote')} htmlFor={`cn-${id}`}>
        <textarea id={`cn-${id}`} className="input min-h-20" maxLength={300} value={note} onChange={(ev) => setNote(ev.target.value)} data-testid="mytime-correct-note" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button type="submit" className="btn btn-primary !min-h-11" disabled={w.busy || !span || !!problem} data-testid="mytime-correct-go">{w.busy ? t('common.saving') : t('myTime.correctGo')}</button>
        <button type="button" className="btn !min-h-11" onClick={onClose} disabled={w.busy}>{t('common.cancel')}</button>
      </div>
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error">{w.error}</Banner>}
    </form>
  )
}
